import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Store, Search, ExternalLink, Share2 } from 'lucide-react';
import PublicCollectionNav from '@/components/PublicCollectionNav';

export default function PublicStore() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [collections, setCollections] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    fetchData();
    
    // Handle window resize for mobile detection
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [slug]);
  
  useEffect(() => {
    if (store) {
      document.title = `${store.name} - Fluencer`;
    }
  }, [store]);
  
  useEffect(() => {
    filterProducts();
  }, [products, selectedCollection, selectedDept, searchQuery]);
  
  const fetchData = async () => {
    try {
      const [storeRes, collectionsRes, deptsRes, productsRes] = await Promise.all([
        fetch(`${API}/public/stores/${slug}`),
        fetch(`${API}/public/stores/${slug}/collections`),
        fetch(`${API}/public/stores/${slug}/departments`),
        fetch(`${API}/public/stores/${slug}/products`),
      ]);
      
      if (!storeRes.ok) throw new Error('Store not found');
      
      const storeData = await storeRes.json();
      const collectionsData = await collectionsRes.json();
      const deptsData = await deptsRes.json();
      const productsData = await productsRes.json();
      
      setStore(storeData);
      setCollections(collectionsData);
      setDepartments(deptsData);
      setProducts(productsData);
      setFilteredProducts(productsData);
      
      // Track page view
      trackPageView(slug);
    } catch (error) {
      toast.error('Store not found');
    } finally {
      setLoading(false);
    }
  };
  
  const trackPageView = async (storeSlug, productId = null) => {
    try {
      await fetch(`${API}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: 'page_view',
          store_slug: storeSlug,
          product_id: productId,
          domain: window.location.hostname
        })
      });
    } catch (e) {
      // Silent fail - analytics shouldn't break UX
    }
  };
  
  const filterProducts = () => {
    let filtered = products;
    
    if (selectedCollection) {
      filtered = filtered.filter(p => p.collection_ids && p.collection_ids.includes(selectedCollection));
    } else if (selectedDept) {
      filtered = filtered.filter(p => p.dept_id === selectedDept);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    setFilteredProducts(filtered);
  };
  
  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Store link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!store) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Store className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-outfit font-bold mb-2">Store Not Found</h1>
          <p className="text-muted-foreground">This store doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }
  
  const themeClasses = {
    minimalist: 'bg-white text-zinc-950',
    hypebeast: 'bg-zinc-950 text-white',
    curator: 'bg-amber-50 text-amber-950',
  };
  
  const accentClasses = {
    minimalist: 'bg-zinc-900 text-white',
    hypebeast: 'bg-lime-400 text-black',
    curator: 'bg-rose-800 text-amber-50',
  };
  
  const themeClass = themeClasses[store.theme_id] || themeClasses.minimalist;
  const accentClass = accentClasses[store.theme_id] || accentClasses.minimalist;
  
  return (
    <div className={`min-h-screen ${themeClass} flex`}>
      {/* Collection Navigation - Only show if enabled */}
      {store?.show_collections_nav && collections.length > 0 && (
        <PublicCollectionNav
          collections={collections}
          selectedCollection={selectedCollection}
          onSelectCollection={(colId) => {
            setSelectedCollection(colId);
            setSelectedDept(null);
          }}
          theme={store.theme_id}
          navTitle={store.collections_nav_title || 'Browse Collections'}
          allProductsLabel={store.all_products_label || 'All Products'}
          productCounts={products.reduce((acc, p) => {
            (p.collection_ids || []).forEach(cid => {
              acc[cid] = (acc[cid] || 0) + 1;
            });
            return acc;
          }, {})}
          isMobile={isMobile}
        />
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
      {/* Header */}
      <header className="border-b sticky top-0 z-50 backdrop-blur-sm bg-opacity-80">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {(store.header_display === 'logo' || store.header_display === 'logo_and_text') && store.logo_url && (
                <img 
                  src={store.logo_url} 
                  alt={store.name} 
                  className="h-10 w-10 object-contain"
                />
              )}
              {(store.header_display === 'text' || store.header_display === 'logo_and_text' || !store.header_display) && (
                <div>
                  <h1 data-testid="store-title" className="text-2xl md:text-3xl font-outfit font-bold">{store.name}</h1>
                  {store.description && (
                    <p className="text-sm opacity-70 mt-1">{store.description}</p>
                  )}
                </div>
              )}
              {store.header_display === 'logo' && store.logo_url && store.description && (
                <p className="text-sm opacity-70">{store.description}</p>
              )}
            </div>
            <Button
              data-testid="share-store-button"
              onClick={handleShare}
              variant="outline"
              size="sm"
              className="rounded-full"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      
      {/* Search & Filters */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 opacity-50" />
            <Input
              data-testid="product-search-input"
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {/* Department Filters */}
        {departments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              data-testid="filter-all-button"
              size="sm"
              variant={selectedDept === null ? 'default' : 'outline'}
              className="rounded-full"
              onClick={() => setSelectedDept(null)}
            >
              All
            </Button>
            {departments.map((dept) => (
              <Button
                key={dept.dept_id}
                data-testid={`filter-dept-${dept.dept_id}`}
                size="sm"
                variant={selectedDept === dept.dept_id ? 'default' : 'outline'}
                className="rounded-full"
                onClick={() => setSelectedDept(dept.dept_id)}
              >
                {dept.name}
              </Button>
            ))}
          </div>
        )}
        
        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg opacity-70">No products found</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.product_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Link
                  to={`/s/${slug}/product/${product.product_id}`}
                  data-testid={`product-card-${product.product_id}`}
                  className="block group"
                >
                  <div className="border rounded-xl overflow-hidden hover:shadow-lg transition-all">
                    {product.images && product.images.length > 0 && (
                      <div className="aspect-square overflow-hidden bg-gray-100">
                        <img
                          src={product.images[0]}
                          alt={product.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-outfit font-bold text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {product.title}
                      </h3>
                      {product.price && (
                        <p className="font-bold mb-2">{product.price}</p>
                      )}
                      {product.recommendation && (
                        <p className="text-sm opacity-70 line-clamp-2">
                          {product.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <footer className="border-t mt-20 py-8">
        <div className="container mx-auto px-4 text-center text-sm opacity-70">
          <p>Powered by <span className="font-outfit font-bold">Fluencer.shop</span></p>
        </div>
      </footer>
      </div>
    </div>
  );
}